import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { Link } from "react-router-dom";
import SeoHead from "@/seo/SeoHead";
import { SITE_URL } from "@/seo/siteSeo";
import { useSiteContent } from "@/cms/SiteContentContext";
import { editableAttrs } from "@/cms/previewUtils";

export default function BlogPage() {
  const { content } = useSiteContent();
  const section = content.blog;
  const posts = section.posts;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "Car Garage Blog",
    url: `${SITE_URL}/blog`,
    blogPost: posts.map((post) => ({
      "@type": "BlogPosting",
      headline: post.title,
      url: `${SITE_URL}/blog/${post.slug}`,
      image: `${SITE_URL}${post.image}`,
      datePublished: post.publishedAt,
      inLanguage: "tr-TR",
    })),
  };

  return (
    <div className="min-h-screen bg-zinc-950">
      <SeoHead title="Blog" description={section.hero.description} path="/blog" image="/images/card.png" jsonLd={jsonLd} />
      <Navbar />

      <section className="relative pt-36 pb-20 overflow-hidden border-b border-zinc-800/70" {...editableAttrs("blog.hero", "object")}>
        <div className="absolute inset-0 z-0 bg-gradient-to-r from-zinc-950 via-[#130b0b] to-zinc-950" />
        <div
          className="absolute inset-0 z-0 opacity-[0.08]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 20%, #dc2626 0%, transparent 35%), radial-gradient(circle at 80% 80%, #dc2626 0%, transparent 30%)",
          }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-2 mb-4 text-sm">
            <Link to="/" className="text-zinc-500 hover:text-red-500 transition-colors">
              Ana Sayfa
            </Link>
            <span className="text-zinc-700">/</span>
            <span className="text-red-500">Blog</span>
          </div>
          <span className="text-red-500 text-xs font-semibold tracking-widest uppercase" style={{ fontFamily: "Rajdhani, sans-serif" }} {...editableAttrs("blog.hero.badge", "text")}>
            {section.hero.badge}
          </span>
          <h1 className="text-5xl md:text-7xl font-bold text-white mt-3 leading-tight" style={{ fontFamily: "Rajdhani, sans-serif" }} {...editableAttrs("blog.hero.title", "text")}>
            {section.hero.title}
          </h1>
          <p className="text-zinc-300 text-lg mt-5 max-w-2xl leading-relaxed" {...editableAttrs("blog.hero.description", "textarea")}>
            {section.hero.description}
          </p>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" {...editableAttrs("blog.posts", "array")}>
            {posts.map((post, index) => (
              <article key={post.id} className="bg-zinc-900 border border-zinc-800 overflow-hidden" {...editableAttrs(`blog.posts.${index}`, "object")}>
                <div className="h-52 overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    {...editableAttrs(`blog.posts.${index}.image`, "image")}
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 text-xs mb-3">
                    <span className="text-red-500 font-semibold tracking-wider uppercase" {...editableAttrs(`blog.posts.${index}.category`, "text")}>
                      {post.category}
                    </span>
                    <span className="text-zinc-600">•</span>
                    <span className="text-zinc-400" {...editableAttrs(`blog.posts.${index}.readTime`, "text")}>
                      {post.readTime}
                    </span>
                  </div>
                  <h2 className="text-white text-2xl font-bold leading-tight mb-3" style={{ fontFamily: "Rajdhani, sans-serif" }} {...editableAttrs(`blog.posts.${index}.title`, "text")}>
                    {post.title}
                  </h2>
                  <p className="text-zinc-400 text-sm leading-relaxed mb-5" {...editableAttrs(`blog.posts.${index}.excerpt`, "textarea")}>
                    {post.excerpt}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-500 text-xs" {...editableAttrs(`blog.posts.${index}.date`, "text")}>
                      {post.date}
                    </span>
                    <Link to={`/blog/${post.slug}`} className="inline-flex items-center gap-2 text-red-500 hover:text-red-400 text-sm font-semibold transition-colors">
                      <span {...editableAttrs("blog.listReadMoreLabel", "text")}>{section.listReadMoreLabel}</span>
                      <i className="ri-arrow-right-line text-sm"></i>
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
